import { useCallback, useEffect, useState } from 'react'
import ExternalStore from '@/services/ExternalStore'
import { useTheme } from '@mui/material'
import { setEnvironment } from '@/store/hederaSettingsSlice'
import { useAppDispatch } from '@/store'
import { logError, Errors } from '@/services/exceptions'
import { type AppKit } from '@reown/appkit'
import { HASH_PACK_WALLET_ID, HEDERA_MAINNET, HEDERA_TESTNET } from '@/config/constants'
import { RouterController } from '@reown/appkit-controllers'
import { type ChainNamespace } from '@reown/appkit-common'
import { type HederaProvider } from '@hashgraph/hedera-wallet-connect'
import { AccountId, AccountBalanceQuery, Client } from '@hiero-ledger/sdk'
import { createHederaAppKit, getHederaProvider, resetHederaProvider } from '@/services/hedera'
import type UniversalProvider from '@walletconnect/universal-provider'
import { mirrorNodeFetch } from '@/services/mirrorNodeCache'

const SUPPORTED_KEY_TYPE = 'ED25519'

type MirrorNodeAccountKey = { _type: string; key: string }
type MirrorNodeAccountResponse = { key?: MirrorNodeAccountKey }

const fetchAccountKeyType = async (accountId: string, chainId: string): Promise<string | null> => {
  try {
    const account = await mirrorNodeFetch<MirrorNodeAccountResponse>(`/api/v1/accounts/${accountId}`, chainId)
    return account?.key?._type ?? null
  } catch {
    return null
  }
}

const { setStore: setRejectionError, useStore: useRejectionError } = new ExternalStore<string | null>(null)

export const useHashPackRejectionError = () => useRejectionError()
export const clearHashPackRejectionError = () => setRejectionError(null)

export type HederaConnectedWallet = {
  label: string
  icon?: string
  address: string
  chainId: string
  provider: HederaProvider
  balance: string
}

const { getStore, setStore, useStore } = new ExternalStore<AppKit>()
const { setStore: setWallet, useStore: useWalletStore } = new ExternalStore<HederaConnectedWallet | null>()

export const initHashPack = async (themeMode: 'light' | 'dark', targetChainId?: string) => {
  if (!getStore()) {
    const appKitInstance = await createHederaAppKit(themeMode, targetChainId)
    setStore(appKitInstance)
  }
}

/**
 * Ensures the HederaProvider's nativeProvider is initialized after session restore.
 *
 * When AppKit restores a WalletConnect session, the provider.namespaces object may be empty
 * even though session.namespaces contains the correct data. This function copies the namespaces
 * from the session to the provider and calls initProviders() to initialize nativeProvider.
 */
const ensureNativeProviderInitialized = (provider: HederaProvider): boolean => {
  if (!provider) return false
  if (provider.nativeProvider) return true

  const providerAny = provider as any

  // Copy namespaces from session to provider if needed
  if (providerAny.session?.namespaces && providerAny.client) {
    const sessionNamespaces = providerAny.session.namespaces
    const sessionNamespaceKeys = Object.keys(sessionNamespaces)
    const providerNamespaceKeys = providerAny.namespaces ? Object.keys(providerAny.namespaces) : []

    const needsNamespaceCopy = sessionNamespaceKeys.length > 0 && !providerNamespaceKeys.includes('hedera')

    if (needsNamespaceCopy) {
      providerAny.namespaces = { ...sessionNamespaces }

      try {
        providerAny.initProviders()
        if (provider.nativeProvider) return true
      } catch {
        // initProviders failed, will return false below
      }
    }
  }

  // Fallback: trigger initProviders via rpcProviders getter
  if (!provider.nativeProvider) {
    try {
      void providerAny.rpcProviders
    } catch {
      // Getter access failed
    }
  }

  return !!provider.nativeProvider
}

export const getConnectedHederaWallet = async (appKitInstance: AppKit): Promise<HederaConnectedWallet | null> => {
  if (!appKitInstance) return null

  try {
    let address: string | undefined
    let isMainnet: boolean
    let provider: HederaProvider | null = null

    const accountInfo = appKitInstance.getAccount?.()
    const appKitChainId = appKitInstance.getChainId()

    if (accountInfo?.address && accountInfo.status === 'connected' && appKitChainId) {
      address = accountInfo.address
      isMainnet = appKitChainId.toString() === 'mainnet'
      provider = appKitInstance.getProvider<HederaProvider>('hedera' as ChainNamespace) ?? null
    } else {
      // Fallback: read from provider session (after direct provider.connect())
      const rawProvider = getHederaProvider()
      if (!rawProvider) return null

      const session = (rawProvider as any).session
      if (!session?.namespaces?.hedera?.accounts?.length) return null

      const firstAccount: string = session.namespaces.hedera.accounts[0]
      const parts = firstAccount.split(':')
      if (parts.length < 3) return null

      address = parts[2]
      isMainnet = parts[1] === 'mainnet'
      provider = rawProvider
    }

    if (!address || !provider) return null
    if (!ensureNativeProviderInitialized(provider)) return null

    const chainId = isMainnet ? HEDERA_MAINNET : HEDERA_TESTNET
    const keyType = await fetchAccountKeyType(address, chainId)
    if (keyType && keyType !== SUPPORTED_KEY_TYPE) {
      logError(
        Errors._107,
        `Account ${address} uses ${keyType} key type. Only ${SUPPORTED_KEY_TYPE} wallets are supported.`,
      )
      setRejectionError(
        `This wallet uses an ${keyType} key. Only ED25519 wallets are supported. Please connect an ED25519 account in HashPack.`,
      )
      await disconnectHashPack()
      return null
    }

    const walletInfo = appKitInstance.getWalletInfo()
    const client = isMainnet ? Client.forMainnet() : Client.forTestnet()
    const balance = await new AccountBalanceQuery().setAccountId(AccountId.fromString(address)).execute(client)

    return {
      label: walletInfo?.name || 'HashPack',
      icon: walletInfo?.icon,
      address,
      chainId: isMainnet ? HEDERA_MAINNET : HEDERA_TESTNET,
      provider,
      balance: balance.hbars.toString(),
    }
  } catch (e) {
    logError(Errors._106, e)
    return null
  }
}

const HASHPACK_WC_WALLET = {
  id: HASH_PACK_WALLET_ID,
  name: 'HashPack',
  homepage: 'https://hashpack.app',
  mobile_link: 'hashpack://',
  desktop_link: 'https://link.hashpack.app',
  // these are removed, webapp was replaced by desktop_link and rdns don't work
  // webapp_link: 'https://wallet.hashpack.app/',
  // rdns: 'com.hashpack.wallet',
}

const openHashPackModal = async (appKitInstance: AppKit) => {
  await appKitInstance.open({ view: 'Connect' })
  RouterController.push('ConnectingWalletConnect', { wallet: HASHPACK_WC_WALLET })
}

let isConnecting = false
export const connectHederaWallet = async (targetChainId?: string): Promise<any> => {
  if (isConnecting) return

  isConnecting = true

  try {
    let appKitInstance = getStore()
    if (!appKitInstance) {
      const instance = await createHederaAppKit('dark', targetChainId)
      setStore(instance)
      appKitInstance = instance
    }

    const currentChainId = appKitInstance.getChainId()
    if (targetChainId && targetChainId !== currentChainId?.toString()) {
      await switchHederaChain(targetChainId || HEDERA_TESTNET)
      return
    }

    await openHashPackModal(appKitInstance)
  } catch (e) {
    logError(Errors._302, e)
  } finally {
    isConnecting = false
  }
}

export const useInitHashPack = (targetChainId?: string) => {
  const theme = useTheme()
  const dispatch = useAppDispatch()

  useEffect(() => {
    dispatch(setEnvironment('hedera'))
    const timeout = setTimeout(() => {
      void initHashPack(theme.palette.mode, targetChainId)
    }, 10)

    return () => clearTimeout(timeout)
  }, [theme, dispatch, targetChainId])
}

export const connectHashPack = (targetChainId?: string) => connectHederaWallet(targetChainId)

// Configuration for session restoration retry mechanism
const MAX_RETRY_COUNT = 3
const INITIAL_RETRY_DELAY = 500
const MAX_RETRY_DELAY = 3000

export const useConnectedHederaWallet = (): HederaConnectedWallet | null => {
  const appKitInstance = useStore()
  const wallet = useWalletStore()
  const [retryCount, setRetryCount] = useState(0)
  const [universalProvider, setUniversalProvider] = useState<UniversalProvider | undefined>(undefined)

  const updateWallet = useCallback(async () => {
    const appKitInstance = getStore()
    if (!appKitInstance) return null

    const connectedWallet = await getConnectedHederaWallet(appKitInstance)
    if (!connectedWallet) return null

    setWallet(connectedWallet)
    setRetryCount(0)
    return connectedWallet
  }, [])

  useEffect(() => {
    const getProvider = async () => {
      const provider = await appKitInstance?.getUniversalProvider()
      setUniversalProvider(provider ?? undefined)
    }
    getProvider()
  }, [appKitInstance])

  // Session restoration on page refresh with exponential backoff
  useEffect(() => {
    if (!appKitInstance || wallet || retryCount >= MAX_RETRY_COUNT) return

    const delay = Math.min(INITIAL_RETRY_DELAY * Math.pow(1.5, retryCount), MAX_RETRY_DELAY)
    const timeout = setTimeout(async () => {
      const result = await updateWallet()
      if (!result) setRetryCount((c) => c + 1)
    }, delay)

    return () => clearTimeout(timeout)
  }, [appKitInstance, retryCount, wallet, updateWallet])

  // Handle connection events from AppKit modal and external disconnects
  useEffect(() => {
    if (!universalProvider) return

    const onConnect = () => setTimeout(updateWallet, 100)
    const onDisconnect = () => setTimeout(disconnectHashPack, 100)

    universalProvider.on('connect', onConnect)
    universalProvider.on('disconnect', onDisconnect)
    return () => {
      universalProvider.off('connect', onConnect)
      universalProvider.off('disconnect', onDisconnect)
    }
  }, [universalProvider, updateWallet])

  return wallet ?? null
}

export const switchHederaChain = async (targetChainId: string) => {
  await disconnectHashPack()

  const appKitInstance = await createHederaAppKit('dark', targetChainId)
  setStore(appKitInstance)

  await openHashPackModal(appKitInstance)
}

export const disconnectHashPack = async () => {
  try {
    const provider = getHederaProvider() as unknown as UniversalProvider
    if (provider?.session) {
      await provider.disconnect()
    }
  } catch {
    // Provider disconnect may fail if session already expired
  }

  const appKitInstance = getStore()
  if (appKitInstance) {
    try {
      await appKitInstance.disconnect()
    } catch {
      // AppKit disconnect may fail if already disconnected via provider
    }
    resetHederaProvider()
    setStore(undefined)
    setWallet(null)
  }
}

export default useStore
